// ./src/hooks/useMarketingChannels.ts
//
// Entity hook for account-level marketing channels (reusable ?ch= traffic-source
// markers). Same state machine as useCouponTypes (see em-scaffold-ui): scoped
// reads, optimistic collection helpers, concurrent-removal tracking, race guards,
// bulk status update. Channels are account-level (no event FK), but an OPTIONAL `eventId`
// is threaded through every op as an AUTHORIZATION CONTEXT (not a filter column): on the
// per-event page it lets crew with event.operate manage the event account's channels; the
// Library page omits it. Every list/create/update/remove call carries it when present.
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";

import {
  MarketingChannel,
  MarketingChannelService,
  MarketingChannelFilter,
  MarketingChannelInput,
  MarketingChannelBulkInput,
  Pagination,
  SortItem,
  listPage,
} from "@/utils/api";
import { upsertBy, upsertManyBy, removeBy, removeManyBy } from "@/utils";
import { useApp } from "@/contexts";
import { DEFAULT_PAGE_SIZE } from "@/config";
import { useRefreshOnWriteTimeout } from "./useRefreshOnWriteTimeout";

// ============================================
// Types
// ============================================

export interface UseMarketingChannelsOptions {
  pageSize?: number;
  initialFilters?: Partial<MarketingChannelFilter>;
  initialSort?: SortItem[];
  autoLoad?: boolean;
  /**
   * The event this list is scoped to (the event marketing page). Channels are ACCOUNT-level, but
   * passing the eventId lets crew with event.operate on it manage the event's account's channels.
   * Threaded into every read/write as an auth context. Omit on the account-level Library page.
   */
  eventId?: string | null;
}

export interface UseMarketingChannelsReturn {
  marketingChannel: MarketingChannel | null;
  marketingChannels: MarketingChannel[];
  loading: boolean;
  isLoaded: boolean;
  isEmpty: boolean;

  loadingMore: boolean;
  hasMore: boolean;
  currentPage: number;
  pageSize: number;

  isCreating: boolean;
  isUpdating: boolean;
  isBulkUpdating: boolean;
  removingIds: Set<string>;
  isRemoving: (id?: string) => boolean;

  filters: Partial<MarketingChannelFilter>;
  sortBy: SortItem[];

  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<MarketingChannelFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  create: (params: MarketingChannelInput) => Promise<OpResult>;
  update: (id: string, params: MarketingChannelInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;
  /** Mass-change status on many marketing channels. */
  bulkUpdate: (ids: string[], params: MarketingChannelBulkInput) => Promise<OpResult>;

  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  reset: () => void;
  getById: (id: string | undefined) => MarketingChannel | undefined;

  totalCount: number;

  lastError: OpResult | null;
  clearError: () => void;
}

// ============================================
// Defaults
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "name", order: "ASC" }];
const DEFAULT_FILTERS: Partial<MarketingChannelFilter> = {};

// ============================================
// Hook
// ============================================

export const useMarketingChannels = (
  options: UseMarketingChannelsOptions = {}
): UseMarketingChannelsReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = true,
    eventId = null,
  } = options;

  // The event auth-context, kept in a ref so the memoized mutations always read the latest.
  const eventIdRef = useRef<string | null>(eventId);
  eventIdRef.current = eventId;

  const { user } = useApp();
  const userId = user?.id ?? null;

  const pageSizeRef = useRef(pageSize);
  // The page size of the LAST read: a refresh re-runs that read, so a WHOLE_SET read stays whole (#118).
  const lastPageSizeRef = useRef<number>(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  const lastQueriedFilterRef = useRef<Partial<MarketingChannelFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  const [marketingChannel, setMarketingChannel] = useState<MarketingChannel | null>(null);
  const [marketingChannels, setMarketingChannels] = useState<MarketingChannel[]>([]);
  /**
   * ⚠⚠ STARTS TRUE WHEN THIS HOOK IS GOING TO AUTO-LOAD — "about to read" is not "read, and empty".
   *
   * The auto-load effect is gated on `userId`, which arrives ASYNCHRONOUSLY from `AppContext`. So on
   * a hard reload or a first navigation there are renders where no request has started yet: with
   * `loading` false and the collection empty, every list component's
   * `!loading && items.length === 0` branch is TRUE and paints the first-run empty state — "No notes
   * yet", "No tasks yet" — before the skeleton and then the data. Reported across the whole event
   * space (owner, 2026-09-28).
   * ⓘ Fixed HERE rather than in the ~45 list components that read it: the lie is in the flag, not in
   * the branch, and a per-component fix would have to be remembered by every list written after it.
   * ⚠ `autoLoad: false` still starts FALSE — a hook told not to load must not claim to be loading.
   */
  const [loading, setLoading] = useState(autoLoad);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  // ROADMAP #112 — the server's count for the current filter (null until read): Load more and the header count.
  const [total, setTotal] = useState<number | null>(null);

  const [filters, setFilters] = useState<Partial<MarketingChannelFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  const [currentPage, setCurrentPage] = useState(1);

  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());

  const [lastError, setLastError] = useState<OpResult | null>(null);

  const isEmpty = useMemo(
    () => isLoaded && marketingChannels.length === 0,
    [isLoaded, marketingChannels.length]
  );

  // ── Removal tracking ──
  const startRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));

      return next;
    });
  }, []);

  const stopRemoving = useCallback((ids: string[]) => {
    setRemovingIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));

      return next;
    });
  }, []);

  const isRemoving = useCallback(
    (id?: string): boolean => (id ? removingIds.has(id) : removingIds.size > 0),
    [removingIds]
  );

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;

    setMarketingChannel(null);
    setMarketingChannels([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    setIsCreating(false);
    setIsUpdating(false);
    setIsBulkUpdating(false);
    setRemovingIds(new Set());
  }, []);

  // ── Core reads ──
  const get = useCallback(async (id: string): Promise<OpResult> => {
    setLoading(true);
    setLastError(null);

    try {
      const result = await MarketingChannelService.get(id);

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const data = (result.data as MarketingChannel[])[0];
        setMarketingChannel(data);
        setMarketingChannels((prev) => upsertBy(prev, data));
        setLastError(null);

        return result;
      }

      setLastError(result);
      setMarketingChannel(null);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);
      setMarketingChannel(null);

      return result;
    } finally {
      setLoading(false);
    }
  }, []);

  const list = useCallback(
    async (
      filterOverride: Partial<MarketingChannelFilter> = {},
      pagination?: Pagination,
      sortByOverride?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      lastPageSizeRef.current = targetPageSize;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
        setLoadingMore(false);
        setCurrentPage(1);
      }

      setLastError(null);

      setFilters(filterOverride);
      lastQueriedFilterRef.current = filterOverride;

      if (sortByOverride) {
        setSortBy(sortByOverride);
        lastQueriedSortRef.current = sortByOverride;
      }

      const currentSortBy = sortByOverride || lastQueriedSortRef.current;

      try {
        const result = await listPage(
          MarketingChannelService.list,
          // Thread the event auth-context so crew reads scope to the event's account.
          { ...(filterOverride as MarketingChannelFilter), ...(eventIdRef.current ? { eventId: eventIdRef.current } : {}) },
          { page: targetPage, pageSize: targetPageSize },
          currentSortBy
        );

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data) ? (result.data as MarketingChannel[]) : [];

          if (isAppending) {
            setMarketingChannels((prev) => upsertManyBy(prev, newItems, "id", "end"));
          } else {
            setMarketingChannels(newItems);
          }

          // ⚠ From the server's total, never "the page came back full" — a full last page offered a Load more that
          // loaded nothing. (An API without `total` keeps the old guess.)
          const serverTotal = typeof result.total === "number" ? result.total : null;
          setTotal(serverTotal);
          setHasMore(serverTotal !== null ? (targetPage - 1) * targetPageSize + newItems.length < serverTotal : newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          setIsLoaded(true);
          setLastError(null);

          return result;
        }

        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setMarketingChannels([]);
        }

        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(result);
        setIsLoaded(true);

        if (!isAppending) {
          setMarketingChannels([]);
        }

        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  // ── Mutations ──
  const create = useCallback(async (params: MarketingChannelInput): Promise<OpResult> => {
    setIsCreating(true);
    setLastError(null);

    try {
      const result = await MarketingChannelService.create({ ...params, ...(eventIdRef.current ? { eventId: eventIdRef.current } : {}) });

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const newItem = (result.data as MarketingChannel[])[0];
        setMarketingChannel(newItem);
        setMarketingChannels((prev) => upsertBy(prev, newItem));
        setTotal((t) => (t !== null ? t + 1 : t));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsCreating(false);
    }
  }, []);

  const update = useCallback(async (id: string, params: MarketingChannelInput): Promise<OpResult> => {
    if (!id) {
      const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Marketing channel ID is required for updating");
      setLastError(result);

      return result;
    }

    setIsUpdating(true);
    setLastError(null);

    try {
      const result = await MarketingChannelService.update({ id }, { ...params, ...(eventIdRef.current ? { eventId: eventIdRef.current } : {}) });

      if (result.code === OP_RESULT_CODES.OK && result.data) {
        const updatedItem = (result.data as MarketingChannel[])[0];
        setMarketingChannel(updatedItem);
        setMarketingChannels((prev) => upsertBy(prev, updatedItem));
        setLastError(null);

        return result;
      }

      setLastError(result);

      return result;
    } catch (error) {
      const result = OpResult.fromException(error);
      setLastError(result);

      return result;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "Marketing channel ID is required for removal");
        setLastError(result);

        return result;
      }

      startRemoving([id]);
      setLastError(null);

      try {
        const result = await MarketingChannelService.remove(id, eventIdRef.current);

        if (result.code === OP_RESULT_CODES.OK) {
          setMarketingChannel(null);
          setMarketingChannels((prev) => removeBy(prev, id));
          setTotal((t) => (t !== null ? Math.max(0, t - 1) : t));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving([id]);
      }
    },
    [startRemoving, stopRemoving]
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one marketing channel must be selected");
        setLastError(result);

        return result;
      }

      startRemoving(ids);
      setLastError(null);

      try {
        const result = await MarketingChannelService.removeMany(ids, eventIdRef.current);

        if (result.code === OP_RESULT_CODES.OK) {
          setMarketingChannel(null);
          setMarketingChannels((prev) => removeManyBy(prev, ids));
          setTotal((t) => (t !== null ? Math.max(0, t - ids.length) : t));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        stopRemoving(ids);
      }
    },
    [startRemoving, stopRemoving]
  );

  const bulkUpdate = useCallback(
    async (ids: string[], params: MarketingChannelBulkInput): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(OP_RESULT_CODES.VALIDATION_FAILED, {}, "At least one marketing channel must be selected");
        setLastError(result);

        return result;
      }

      setIsBulkUpdating(true);
      setLastError(null);

      try {
        const result = await MarketingChannelService.bulkUpdate(ids, { ...params, ...(eventIdRef.current ? { eventId: eventIdRef.current } : {}) });

        if (result.code === OP_RESULT_CODES.OK) {
          const updatedItems = Array.isArray(result.data) ? (result.data as MarketingChannel[]) : [];
          setMarketingChannels((prev) => upsertManyBy(prev, updatedItems));
          setLastError(null);

          return result;
        }

        setLastError(result);

        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(result);

        return result;
      } finally {
        setIsBulkUpdating(false);
      }
    },
    []
  );

  // ── Convenience ──
  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) {
      return;
    }

    const nextPage = currentPage + 1;

    await list(
      lastQueriedFilterRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loading, loadingMore, hasMore, currentPage]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFilterRef.current,
      { page: 1, pageSize: lastPageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(refresh, isLoaded);

  const getById = useCallback(
    (id: string | undefined): MarketingChannel | undefined => {
      if (!id) {
        return undefined;
      }

      return marketingChannels.find((item) => item.id === id);
    },
    [marketingChannels]
  );

  const totalCount = useMemo(() => total ?? marketingChannels.length, [total, marketingChannels]);

  // ── Effects ──
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }

      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
      }
    }
  }, [userId, autoLoad, list, reset]);

  // Re-list when the event context changes (a crew member navigating event A → B while the provider
  // stays mounted). The write ops read eventIdRef, but the loaded list must also re-scope to the new
  // event's account — without this the page keeps showing the previous event's channels until refresh.
  const previousEventIdRef = useRef<string | null>(eventId);
  useEffect(() => {
    const previousEventId = previousEventIdRef.current;
    previousEventIdRef.current = eventId;

    if (previousEventId === eventId) {
      return;
    }

    if (autoLoad && userId) {
      reset();
      list(initialFiltersRef.current, { page: 1, pageSize: pageSizeRef.current }, initialSortRef.current, true);
    }
  }, [eventId, autoLoad, userId, list, reset]);

  // ── Return ──
  return useMemo(
    () => ({
      marketingChannel,
      marketingChannels,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      pageSize: pageSizeRef.current,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    }),
    [
      marketingChannel,
      marketingChannels,
      loading,
      isLoaded,
      isEmpty,
      loadingMore,
      hasMore,
      currentPage,
      isCreating,
      isUpdating,
      isBulkUpdating,
      removingIds,
      isRemoving,
      filters,
      sortBy,
      get,
      list,
      create,
      update,
      remove,
      removeMany,
      bulkUpdate,
      loadMore,
      refresh,
      reset,
      getById,
      totalCount,
      lastError,
      clearError,
    ]
  );
};
